/**
 * SubtaskItem - Individual Subtask Component
 * Displays a single subtask with status, AI support, and interactive elements
 */

import React from 'react';
import { Check, Lock, Sparkles, Clock, Info } from 'lucide-react';
import {
  isOverdue,
  getDaysUntilDue,
  formatDueDate,
  getTimeEstimate,
  formatTimeEstimate
} from '../../utils/taskUtils';

const SubtaskItem = ({
  subtask,
  onComplete,
  onAISupport,
  onTaskClick,
  className = ''
}) => {
  const isComplete = subtask.status === 'completed';
  const isLocked = subtask.is_locked;
  const isCurrent = !isComplete && !isLocked && subtask.workflow_phase === 'ready';
  const overdue = isOverdue(subtask);
  const daysUntil = getDaysUntilDue(subtask);
  const dueDateText = formatDueDate(subtask.due_date);
  const timeEstimate = getTimeEstimate(subtask);
  const taskTip = subtask.task_tip || subtask.metadata?.task_tip;
  const aiHelpText = subtask.ai_help_text || subtask.metadata?.ai_help_text;
  const hasAI = subtask.can_autogenerate && subtask.can_autogenerate !== 'no';

  // Determine styling based on state
  const containerBg = isComplete
    ? 'bg-gray-100'
    : isCurrent
    ? 'bg-green-50 border-l-4 border-l-green-500'
    : isLocked
    ? 'bg-gray-50'
    : 'bg-white';

  const titleStyle = isComplete
    ? 'text-gray-500 line-through'
    : isLocked
    ? 'text-gray-400'
    : 'text-gray-900';

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    if (!isLocked && !isComplete && onComplete) {
      onComplete(subtask);
    }
  };

  const handleAISupportClick = (e) => {
    e.stopPropagation();
    if (!isLocked && onAISupport) {
      onAISupport(subtask);
    }
  };

  const handleClick = () => {
    if (onTaskClick) {
      onTaskClick(subtask);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`${containerBg} rounded-lg border border-gray-200 p-3 transition-all hover:shadow-md cursor-pointer ${className}`}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <button
          onClick={handleCheckboxClick}
          disabled={isLocked}
          className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all mt-0.5 ${
            isComplete
              ? 'bg-green-500 border-green-500'
              : isLocked
              ? 'bg-gray-200 border-gray-300 cursor-not-allowed'
              : 'border-gray-300 hover:border-green-500'
          }`}
        >
          {isComplete && <Check className="w-4 h-4 text-white" />}
          {isLocked && <Lock className="w-3 h-3 text-gray-400" />}
        </button>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Title and Badges Row */}
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex-1">
              <h4 className={`text-sm font-medium ${titleStyle}`}>
                {subtask.title}
              </h4>
            </div>

            {/* Status Badges */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {/* AI Capability Badge */}
              {subtask.can_autogenerate && subtask.can_autogenerate !== 'no' && !isComplete && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-700 text-xs font-medium rounded">
                  <Sparkles className="w-2.5 h-2.5" />
                  {subtask.can_autogenerate === 'partial' ? 'AI Assist' : 'AI'}
                </span>
              )}

              {/* Current Badge */}
              {isCurrent && (
                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                  Current
                </span>
              )}

              {/* Locked Badge */}
              {isLocked && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-200 text-gray-600 text-xs font-medium rounded-full">
                  <Lock className="w-3 h-3" />
                  Locked
                </span>
              )}
            </div>
          </div>

          {/* Metadata Row */}
          <div className="flex items-center gap-3 text-xs text-gray-600 mb-2">
            {/* Due Date */}
            {subtask.due_date && (
              <span className={`flex items-center gap-1 ${
                overdue
                  ? 'text-red-600 font-medium'
                  : daysUntil !== null && daysUntil <= 3
                  ? 'text-orange-600 font-medium'
                  : 'text-gray-600'
              }`}>
                <Clock className="w-3 h-3" />
                {overdue && `${Math.abs(daysUntil)}d overdue - `}
                Due {dueDateText}
              </span>
            )}

            {/* Time Estimate */}
            {timeEstimate && (
              <span className="text-gray-500">
                {formatTimeEstimate(timeEstimate)}
              </span>
            )}
          </div>

          {/* Task Tip */}
          {taskTip && !isComplete && (
            <div className="flex items-start gap-1.5 p-2 bg-blue-50 border border-blue-100 rounded text-xs text-blue-900 mb-2">
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-blue-600" />
              <p className="flex-1">{taskTip}</p>
            </div>
          )}

          {/* AI Help Text */}
          {aiHelpText && hasAI && !isComplete && (
            <div className="flex items-start gap-1.5 p-2 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded text-xs text-purple-900 mb-2">
              <Sparkles className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-purple-600" />
              <p className="flex-1">
                <span className="font-semibold">AI can help: </span>
                {aiHelpText}
              </p>
            </div>
          )}

          {/* AI Support Button */}
          {!isComplete && !isLocked && onAISupport && (
            <button
              onClick={handleAISupportClick}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-xs font-medium rounded-md hover:from-purple-600 hover:to-indigo-600 transition-all shadow-sm hover:shadow group"
            >
              <Sparkles className="w-3.5 h-3.5 animate-pulse" />
              <span>AI Support</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubtaskItem;
